// Phase 4 · M1 · Absence & Late Notices client library.
//
// Parents declare INTENT (absent / late). Teachers + admins observe.
// Auto-superseded by trigger when a teacher records attendance.

import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { todayStr } from "@/lib/attendance";

const BASE_SELECT = `
  id, organization_id, child_id, family_id, classroom_id,
  notice_type, notice_date, expected_arrival_time, reason,
  status, submitted_by, submitted_at, cancelled_at,
  superseded_at, superseded_by
`;

const SUBMITTED_BY_PROFILE = `
  submitter:profiles!attendance_notices_submitted_by_fkey(id, full_name, email)
`;

export const NOTICE_TYPES = ["absent", "late"];

/**
 * Submit a "child will be absent" notice. Idempotent on (child, date,
 * status='submitted') via the partial unique index — caller should
 * check for existing submitted notice first or treat 23505 as "already
 * filed".
 */
export async function submitAbsenceNotice({
  childId,
  familyId,
  classroomId,
  organizationId,
  noticeDate,
  reason,
  submittedBy,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!childId || !familyId || !organizationId || !submittedBy) {
    throw new Error("Missing required fields");
  }
  const { data, error } = await supabase
    .from("attendance_notices")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      family_id: familyId,
      classroom_id: classroomId || null,
      notice_type: "absent",
      notice_date: noticeDate || todayStr(),
      reason: reason?.trim() || null,
      submitted_by: submittedBy,
    })
    .select(BASE_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Submit a "running late today" notice with optional expected arrival.
 */
export async function submitLateNotice({
  childId,
  familyId,
  classroomId,
  organizationId,
  noticeDate,
  expectedArrivalTime,
  reason,
  submittedBy,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("attendance_notices")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      family_id: familyId,
      classroom_id: classroomId || null,
      notice_type: "late",
      notice_date: noticeDate || todayStr(),
      expected_arrival_time: expectedArrivalTime || null,
      reason: reason?.trim() || null,
      submitted_by: submittedBy,
    })
    .select(BASE_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/** Parent cancels a submitted notice. Cannot cancel post-supersede. */
export async function cancelNotice(noticeId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("attendance_notices")
    .update({
      status: "cancelled",
      cancelled_at: new Date().toISOString(),
    })
    .eq("id", noticeId)
    .eq("status", "submitted")
    .select(BASE_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/** Parent — list active + recent notices for a family. */
export async function listFamilyNotices({ familyId, sinceDate } = {}) {
  if (!isSupabaseConfigured || !familyId) return [];
  const cutoff = sinceDate || daysAgoStr(14);
  const { data, error } = await supabase
    .from("attendance_notices")
    .select(`
      ${BASE_SELECT},
      child:children(id, first_name, last_name)
    `)
    .eq("family_id", familyId)
    .gte("notice_date", cutoff)
    .order("notice_date", { ascending: false });
  if (error) {
    if (error.code === "42P01") return []; // table not yet migrated
    throw error;
  }
  return data || [];
}

/** Teacher — submitted notices for a classroom on a specific date. */
export async function listClassroomNoticesForDate({ classroomId, date }) {
  if (!isSupabaseConfigured || !classroomId) return [];
  const { data, error } = await supabase
    .from("attendance_notices")
    .select(`${BASE_SELECT}, child:children(id, first_name, last_name)`)
    .eq("classroom_id", classroomId)
    .eq("notice_date", date || todayStr())
    .eq("status", "submitted")
    .order("notice_type", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

/** Admin — all submitted notices for the org on a specific date. */
export async function listOrgNoticesForDate(date) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("attendance_notices")
    .select(`
      ${BASE_SELECT},
      child:children(id, first_name, last_name),
      classroom:classrooms(id, name),
      family:families(id, display_name),
      ${SUBMITTED_BY_PROFILE}
    `)
    .eq("notice_date", date || todayStr())
    .eq("status", "submitted")
    .order("notice_type", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

/** Look up an existing submitted notice for (child, date) — used for idempotent UI. */
export async function findActiveNotice({ childId, date }) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase
    .from("attendance_notices")
    .select(BASE_SELECT)
    .eq("child_id", childId)
    .eq("notice_date", date || todayStr())
    .eq("status", "submitted")
    .maybeSingle();
  if (error) {
    if (error.code === "42P01") return null;
    throw error;
  }
  return data;
}

// ---------- helpers ----------

export function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function isPastDate(yyyyMmDd) {
  if (!yyyyMmDd) return false;
  const today = todayStr();
  return yyyyMmDd < today;
}

export function formatNoticeDate(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  const today = todayStr();
  if (yyyyMmDd === today) return "Today";
  // Tomorrow check
  const t = new Date();
  t.setDate(t.getDate() + 1);
  const tom = t.toISOString().slice(0, 10);
  if (yyyyMmDd === tom) return "Tomorrow";
  return new Date(yyyyMmDd + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatLocalTime(hhmm) {
  if (!hhmm) return null;
  // hhmm may be "HH:MM" or "HH:MM:SS" — only use first 5 chars.
  const [h, m] = hhmm.slice(0, 5).split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m), 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export const NOTICE_LABELS = {
  absent: "Absent",
  late: "Running late",
};
